import { RootState } from "store";
import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import {
  defaultMetaObj,
  deleteErrorResponse,
  initialInventoryState,
  propertyDeleteErrorModified,
} from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchAllDropdownProjects } from "./dropdownDataSlice";

const { inventoryEndPoint, tokenEndPoint } = endPoints;

export const fetchAllInventory = createAsyncThunk(
  "inventory/fetchAllInventory",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue, getState, dispatch },
  ) => {
    const state = getState() as RootState;
    let projects = state?.dropdownLeadReducer?.data?.projects?.objects || [];

    if (!projects?.length) {
      const action = await dispatch(fetchAllDropdownProjects({ page: 1 }));

      projects = action?.payload?.objects || [];
    }

    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: inventoryEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return { ...response.data, projects };
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  },
);

export const deleteInventory = createAsyncThunk(
  "inventory/deleteInventory",
  async ({ inventoryId }: any, { rejectWithValue }) => {
    try {
      const paginatedUrl = getPaginatedUrlWithSearchParams({
        pageNumber: 1,
        limit: 1,
        endpoint: tokenEndPoint,
        searchParams: {
          property_unit__id: inventoryId,
        },
      });

      const tokenResponse: AxiosResponse =
        await crudService.getAll(paginatedUrl);

      if (
        tokenResponse.status === 200 &&
        tokenResponse.data?.meta?.total_count > 0
      ) {
        NotificationManager.error(propertyDeleteErrorModified);

        return false;
      }
      const response: any = await crudService.delete(
        inventoryEndPoint,
        inventoryId,
      );

      if (response.status === 204) {
        NotificationManager.success("Inventory Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(
        errorMessage?.includes(deleteErrorResponse)
          ? propertyDeleteErrorModified
          : errorMessage,
      );
      return rejectWithValue(error.message);
    }
  },
);

export const addNewInventory = createAsyncThunk(
  "inventory/addNewInventory",
  async (newInventory: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${inventoryEndPoint}/`,
        newInventory,
      );

      if (response.status === 201) {
        NotificationManager.success("Inventory Created Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  },
);

export const updateInventory = createAsyncThunk(
  "inventory/updateInventory",
  async ({ updatedInventory, inventoryId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        inventoryEndPoint,
        inventoryId,
        updatedInventory,
      );
      if (response.status === 204) {
        NotificationManager.success("Inventory updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  },
);

const inventorySlice = createSlice({
  name: "inventory",
  initialState: initialInventoryState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all inventory
    builder
      .addCase(fetchAllInventory.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllInventory.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.inventory = action.payload?.objects.map((item: any) => {
            return {
              ...item,
              project: action.payload?.projects?.find(
                (pro: any) => pro?.project_id === item?.project_id,
              ),
            };
          });
          state.meta = action.payload?.meta;
        },
      )
      .addCase(
        fetchAllInventory.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.inventory = [];
          state.meta = defaultMetaObj;
        },
      );

    // delete inventory
    builder
      .addCase(deleteInventory.pending, (State) => {
        State.loading = true;
      })
      .addCase(
        deleteInventory.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        },
      )
      .addCase(
        deleteInventory.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        },
      );

    // add new inventory
    builder
      .addCase(addNewInventory.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        addNewInventory.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        },
      )
      .addCase(addNewInventory.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    //   update inventory
    builder
      .addCase(updateInventory.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateInventory.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        },
      )
      .addCase(
        updateInventory.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        },
      );
  },
});

export const inventoryReducer = inventorySlice.reducer;
